import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, MapPinIcon, SearchIcon } from 'lucide-react';
interface ServicePickerProps {
  onSelectService: (serviceSlug: string, zip: string) => void;
  onClose: () => void;
}
const SERVICE_OPTIONS = [{
  label: 'Walk-In Tubs',
  slug: 'walk-in-tubs',
  group: 'Bathroom'
}, {
  label: 'Bathroom Remodel',
  slug: 'bathroom-remodel',
  group: 'Bathroom'
}, {
  label: 'Kitchen Remodel',
  slug: 'kitchen-remodel',
  group: 'Kitchen'
}, {
  label: 'Roofing',
  slug: 'roofing',
  group: 'Exterior'
}, {
  label: 'Siding',
  slug: 'siding',
  group: 'Exterior'
}, {
  label: 'Window Replacement',
  slug: 'window-replacement',
  group: 'Exterior'
}, {
  label: 'Doors',
  slug: 'doors',
  group: 'Exterior'
}, {
  label: 'Gutter Guards',
  slug: 'gutter-guards',
  group: 'Exterior'
}, {
  label: 'HVAC',
  slug: 'hvac',
  group: 'Systems'
}, {
  label: 'Plumbing',
  slug: 'plumbing',
  group: 'Systems'
}, {
  label: 'Electrical',
  slug: 'electrical',
  group: 'Systems'
}, {
  label: 'Solar Panels',
  slug: 'solar',
  group: 'Systems'
}, {
  label: 'Home Security',
  slug: 'home-security',
  group: 'Systems'
}, {
  label: 'Flooring',
  slug: 'flooring',
  group: 'Interior'
}, {
  label: 'Stairlifts',
  slug: 'stairlifts',
  group: 'Interior'
}, {
  label: 'Pest Control',
  slug: 'pest-control',
  group: 'Outdoor & Other'
}];
const GROUPS = ['Bathroom', 'Kitchen', 'Exterior', 'Systems', 'Interior', 'Outdoor & Other'];
export function ServicePicker({
  onSelectService,
  onClose
}: ServicePickerProps) {
  const [selectedService, setSelectedService] = useState('');
  const [zip, setZip] = useState('');
  const [error, setError] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService) {
      setError('Please select a service');
      return;
    }
    if (!zip || zip.length < 5) {
      setError('Please enter a valid 5-digit zip code');
      return;
    }
    setError('');
    if (typeof window !== 'undefined') {
      window._mgq = window._mgq || [];
      window._mgq.push(['MgSensorInvoke', 'initial_click']);
    }
    onSelectService(selectedService, zip);
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-3084">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-3085">
        <img src="https://cdn.magicpatterns.com/uploads/ub1pkcCgYFRF8nZfqS1Kva/ChatGPT_Image_May_14,_2026,_01_49_52_PM_1.svg" alt="FindServiceQuotes" className="h-8 md:h-9 w-auto" data-id="element-3086" />
        <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-3087">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3088" />
          <span className="hidden sm:inline" data-id="element-3089">Back to Home</span>
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12" data-id="element-3090">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="w-full max-w-xl" data-id="element-3091">
          <div className="text-center mb-10" data-id="element-3092">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-2xl mb-6" data-id="element-3093">
              <SearchIcon className="w-8 h-8 text-amber-600" data-id="element-3094" />
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3" data-id="element-3095">
              Get Your Free Quotes
            </h1>
            <p className="text-lg text-slate-500" data-id="element-3096">
              Select a service and enter your zip code to get matched with
              top-rated local pros.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8 space-y-6" data-id="element-3097">
            {/* Service Selection */}
            <div data-id="element-3098">
              <label htmlFor="service-select" className="block text-sm font-bold text-slate-700 mb-2" data-id="element-3099">
                What do you need help with?
              </label>
              <select id="service-select" value={selectedService} onChange={e => {
              setSelectedService(e.target.value);
              setError('');
            }} className="w-full h-14 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent appearance-none text-base" data-id="element-3100">
                <option value="" disabled data-id="element-3101">
                  Select a service...
                </option>
                {GROUPS.map(group => <optgroup key={group} label={group} data-id="element-3102">
                    {SERVICE_OPTIONS.filter(s => s.group === group).map(s => <option key={s.slug} value={s.slug} data-id="element-3103">
                          {s.label}
                        </option>)}
                  </optgroup>)}
              </select>
            </div>

            {/* Zip Code */}
            <div data-id="element-3104">
              <label htmlFor="zip-input" className="block text-sm font-bold text-slate-700 mb-2" data-id="element-3105">
                Your Zip Code
              </label>
              <div className="relative" data-id="element-3106">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3107">
                  <MapPinIcon className="w-5 h-5 text-slate-400" data-id="element-3108" />
                </div>
                <input type="text" id="zip-input" value={zip} onChange={e => {
                setZip(e.target.value.replace(/\D/g, '').slice(0, 5));
                setError('');
              }} placeholder="Enter your zip code" maxLength={5} className="w-full h-14 pl-12 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base" data-id="element-3109" />
              </div>
            </div>

            {/* Error */}
            {error && <p className="text-red-500 text-sm font-medium" data-id="element-3110">{error}</p>}

            {/* Submit */}
            <button type="submit" className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2" data-id="element-3111">
              Get Free Quotes
              <ArrowRightIcon className="w-5 h-5" data-id="element-3112" />
            </button>

            <p className="text-xs text-slate-400 text-center" data-id="element-3113">
              Free, no-obligation quotes from vetted professionals.
            </p>
          </form>
        </motion.div>
      </main>
    </div>;
}